import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';
import { dateTime, humanise, mobile as fmtMobile } from '../lib/format';
import { DataTable, useListParams } from '../components/DataTable';
import type { Column } from '../components/DataTable';
import { Avatar, Badge, Button, Chips, Field, Modal, Notice, StatusBadge, Textarea, useToast } from '../components/ui';

interface KycRow {
  id: string;
  docType: string;
  status: string;
  nameOnDoc: string | null;
  docLast4: string | null;
  vendorName: string;
  vendorRef: string;
  rejectReason: string | null;
  createdAt: string;
  user: { id: string; mobile: string; displayName: string | null; roles: string[] };
}

/**
 * FR-KYC review queue.
 *
 * Note what is shown: the vendor's reference, the name it returned, and the
 * last four digits. There is no document image and no full ID number to show
 * — §7.3 forbids storing them, so a reviewer approves on the vendor's verdict
 * plus the name matching, which is the whole point of using a KUA.
 */
export function KycPage() {
  const { get, set } = useListParams({ status: 'PENDING' });
  const status = get('status');
  const [rejecting, setRejecting] = useState<KycRow | null>(null);
  const [reason, setReason] = useState('');
  const toast = useToast();
  const qc = useQueryClient();

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ['kyc'] });
    qc.invalidateQueries({ queryKey: ['dashboard-badges'] });
    qc.invalidateQueries({ queryKey: ['dashboard'] });
  };

  const approve = useMutation({
    mutationFn: (id: string) => api.patch(`/admin/kyc/${id}/approve`),
    onSuccess: () => {
      toast('Verification approved', 'success');
      invalidate();
    },
    onError: (e: Error) => toast(e.message, 'error'),
  });

  const reject = useMutation({
    mutationFn: ({ id, reason }: { id: string; reason: string }) =>
      api.patch(`/admin/kyc/${id}/reject`, { reason }),
    onSuccess: () => {
      toast('Verification rejected', 'success');
      setRejecting(null);
      setReason('');
      invalidate();
    },
    onError: (e: Error) => toast(e.message, 'error'),
  });

  const columns: Array<Column<KycRow>> = [
    {
      key: 'user',
      header: 'Person',
      render: (r) => (
        <div className="row">
          <Avatar name={r.user.displayName ?? r.nameOnDoc} />
          <div>
            <div className="cell-strong">{r.user.displayName ?? r.nameOnDoc ?? 'Unnamed'}</div>
            <div className="cell-muted">{fmtMobile(r.user.mobile)}</div>
          </div>
        </div>
      ),
    },
    {
      key: 'doc',
      header: 'Document',
      render: (r) => (
        <div>
          <div className="cell-strong">{humanise(r.docType)}</div>
          <div className="cell-muted">{r.docLast4 ? `ends ${r.docLast4}` : 'no digits returned'}</div>
        </div>
      ),
    },
    {
      key: 'name',
      header: 'Name on document',
      render: (r) => r.nameOnDoc ?? <span className="cell-muted">—</span>,
    },
    {
      key: 'vendor',
      header: 'Vendor',
      render: (r) => (
        <div>
          <div>{r.vendorName}</div>
          <div className="cell-mono cell-clip" title={r.vendorRef}>
            {r.vendorRef}
          </div>
        </div>
      ),
    },
    { key: 'roles', header: 'Roles', render: (r) => <div className="row" style={{ gap: 4 }}>{r.user.roles.map((role) => <Badge key={role} tone="neutral">{humanise(role)}</Badge>)}</div> },
    { key: 'status', header: 'Status', render: (r) => <StatusBadge value={r.status} /> },
    { key: 'when', header: 'Submitted', render: (r) => <span className="cell-muted">{dateTime(r.createdAt)}</span> },
    {
      key: 'actions',
      header: '',
      render: (r) =>
        r.status === 'PENDING' ? (
          <div className="cell-actions">
            <Button
              size="sm"
              variant="success"
              loading={approve.isPending && approve.variables === r.id}
              onClick={() => approve.mutate(r.id)}
            >
              Approve
            </Button>
            <Button size="sm" variant="ghost" onClick={() => setRejecting(r)}>
              Reject
            </Button>
          </div>
        ) : r.rejectReason ? (
          <span className="cell-muted cell-clip" title={r.rejectReason}>
            {r.rejectReason}
          </span>
        ) : null,
    },
  ];

  return (
    <div className="stack">
      <Notice tone="info">
        Approving grants the earning actions of a user's role — posting listings, going live as a provider, enrolling
        partners. No document image or full ID number is stored, so review on the vendor's verdict and the returned name.
      </Notice>

      <DataTable<KycRow>
        queryKey={['kyc', status]}
        path="/admin/kyc"
        params={{ status }}
        columns={columns}
        emptyIcon="✅"
        emptyTitle={status === 'PENDING' ? 'Nothing waiting for review' : 'No records with this status'}
        emptyBody={status === 'PENDING' ? 'Every submitted verification has been decided.' : undefined}
        toolbar={
          <Chips
            value={status}
            onChange={(v) => set('status', v)}
            options={[
              { value: 'PENDING', label: 'Awaiting review' },
              { value: 'VERIFIED', label: 'Verified' },
              { value: 'REJECTED', label: 'Rejected' },
            ]}
          />
        }
      />

      {rejecting && (
        <Modal
          title="Reject this verification"
          sub={`${rejecting.user.displayName ?? rejecting.nameOnDoc ?? 'This user'} will be told why, and can submit again.`}
          onClose={() => setRejecting(null)}
          footer={
            <>
              <Button onClick={() => setRejecting(null)}>Cancel</Button>
              <Button
                variant="danger"
                loading={reject.isPending}
                disabled={!reason.trim()}
                onClick={() => reject.mutate({ id: rejecting.id, reason: reason.trim() })}
              >
                Reject verification
              </Button>
            </>
          }
        >
          <Field label="Reason" hint="Shown to the user, so make it actionable.">
            <Textarea
              value={reason}
              autoFocus
              placeholder="e.g. The name on the document does not match the account name."
              onChange={(e) => setReason(e.target.value)}
            />
          </Field>
        </Modal>
      )}
    </div>
  );
}
